import {
    isRouteErrorResponse,
    Links,
    Meta,
    Outlet,
    Scripts,
    ScrollRestoration,
    useLoaderData,
} from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import { Toaster } from "@/components/ui/sonner";
import "react-quill-new/dist/quill.snow.css";
import type { Route } from "./+types/root";
import "./app.css";

import { ThemeProvider, useTheme, PreventFlashOnWrongTheme, Theme } from "remix-themes";
import { getUserPermissions } from "./lib/permissions.server";
import { themeSessionResolver } from "~/lib/theme.server";
import { getUser } from "@/lib/user.server";
import type { Socket } from "socket.io-client";
import io from "socket.io-client";

import { NotificationProvider, SocketProvider, useSocket, useNotifications } from "~/context";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const links: Route.LinksFunction = () => [
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
    },
    {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
    },
];

// Loader to fetch current user, permissions, and theme
export async function loader({ request }: LoaderFunctionArgs) {
    const currentUser = await getUser(request);
    let permissions: string[] = [];
    if (currentUser) {
        permissions = await getUserPermissions(currentUser.id);
    }

    const { getTheme } = await themeSessionResolver(request);
    const theme = getTheme(); // 'light' | 'dark' | null

    return { currentUser, permissions, theme };
}

// Html layout component (must be inside ThemeProvider to use useTheme)
function Html({ children }: { children: React.ReactNode }) {
    const [theme, setTheme] = useTheme();

    // Empty deps: runs once on mount only. Must NOT depend on [theme, setTheme] —
    // setTheme's identity changes with theme, which would re-fire this effect
    // right after it runs, looping.
    //
    // The `theme !== null` guard is required, not optional: theme is only null
    // on a device that has never had a preference stored (first visit, or after
    // clearing cookies). Once the user has an explicit choice — either from a
    // manual toggle, or from this same effect running once before — theme is
    // non-null, and this effect must leave it alone. Without the guard, every
    // page load would silently overwrite a manually-chosen theme back to
    // whatever the device currently prefers.
    useEffect(() => {
        if (theme !== null) return;

        const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        setTheme(isDarkMode ? Theme.DARK : Theme.LIGHT);
    }, []);

    return (
        // Hardcoding a "light" fallback here would mask the null (no stored
        // preference) state from PreventFlashOnWrongTheme's own system-theme
        // detection, so it never got a chance to apply the OS/device preference.
        <html lang="en" className={theme ?? ""}>
            <head>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <Meta />
                <Links />
                <PreventFlashOnWrongTheme ssrTheme={Boolean(theme)} />
            </head>
            <body>
                <>
                    {children}
                    <Toaster position="top-right" richColors />
                    <ScrollRestoration />
                    <Scripts />
                </>

            </body>
        </html>
    );
}

// Single socket-to-notification bridge — renders once inside both providers
function NotificationSocketBridge({ userId }: { userId?: number }) {
    const socket = useSocket();
    const { prependNotification } = useNotifications();

    useEffect(() => {
        if (!socket || !userId) return;

        const identify = () => socket.emit("identify", userId);
        identify();
        socket.on("connect", identify);

        const handler = (notification: any) => {
            prependNotification(notification);
            toast.success(notification.title, {
                description: notification.message,
                duration: 6000,
            });
        };

        socket.on("new_notification", handler);

        return () => {
            socket.off("connect", identify);
            socket.off("new_notification", handler);
        };
    }, [socket, userId]);

    return null;
}

// Root App
export default function App() {
    const { currentUser, theme } = useLoaderData<typeof loader>();
    const userId = currentUser?.id;

    const [socket, setSocket] = useState<Socket>();

    useEffect(() => {
        const socket = io();
        setSocket(socket);
        return () => {
            socket.close();
        };
    }, []);

    useEffect(() => {
        if (!socket) return;
        socket.on("confirmation", (data) => {
            console.log(data);
        });
    }, [socket]);

    return (
        <ThemeProvider
            specifiedTheme={theme}
            themeAction="/action/set-theme"
        >
            <Html>
                <SocketProvider socket={socket as any}>
                    <NotificationProvider>
                        <NotificationSocketBridge userId={userId} />
                        <Outlet context={{ currentUser }} />
                    </NotificationProvider>
                </SocketProvider>
            </Html>
        </ThemeProvider>
    );
}

// Error Boundary
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
    let message = "Oops!";
    let details = "An unexpected error occurred.";
    let stack: string | undefined;

    if (isRouteErrorResponse(error)) {
        message = error.status === 404 ? "404" : "Error";
        details =
            error.status === 404
                ? "The requested page could not be found."
                : error.statusText || details;
    } else if (import.meta.env.DEV && error && error instanceof Error) {
        details = error.message;
        stack = error.stack;
    }

    return (
        <main className="pt-16 p-4 container mx-auto">
            <h1>{message}</h1>
            <p>{details}</p>
            {stack && (
                <pre className="w-full p-4 overflow-x-auto">
                    <code>{stack}</code>
                </pre>
            )}
        </main>
    );
}

